import { useRouter } from "next/router";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { type ReactElement, Suspense } from "react";
import dynamic from "next/dynamic";

import AppLayout from "../components/common/AppLayout";
import { CenteredInfinityLoader } from "../rebrand/molecules/Loaders/InfinityLoader";
import AuthMiddleware from "../rebrand/organisms/auth/AuthMiddleware";
import ProfessionalDashboard from "../rebrand/pages/ProfessionalDashboard";
import { useUserData } from "../services/contexts/UserDataContext";
import { useChatContext } from "../services/contexts/ChatContext";
import { PAGE_ROUTE } from "../utils/routing/routes";
import HomePage from "../rebrand/pages/Home";

// Disable SSR for ChatLoader since it likely uses document/window
const ChatLoader = dynamic(
  () => import("../rebrand/organisms/ChatChannel/components/ChatLoader"),
  {
    ssr: false,
    loading: () => <CenteredInfinityLoader height="full" />,
  },
);

function IndexComponent() {
  const { push } = useRouter();
  const { data: user } = useUserData();

  if (user?.organisation?.hasTeamSessionsOnly) {
    push(PAGE_ROUTE.ORGANISATION.TEAM_SESSIONS.HOME);
    return;
  }

  if (user?.role?.includes("MHP")) {
    return <ProfessionalDashboard />;
  }

  return <HomePage />;
}

export default function IndexPage(): ReactElement {
  const { isCreatingThread } = useChatContext();

  if (isCreatingThread) {
    return (
      <AuthMiddleware roles={[]}>
        <AppLayout showSidebar={false}>
          <ChatLoader />
        </AppLayout>
      </AuthMiddleware>
    );
  }

  return (
    <AuthMiddleware roles={[]}>
      <AppLayout>
        <Suspense fallback={<CenteredInfinityLoader height="full" />}>
          <IndexComponent />
        </Suspense>
      </AppLayout>
    </AuthMiddleware>
  );
}

export async function getServerSideProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale)),
    },
  };
}
